import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import ActivityCard from "@/components/app/ActivityCard";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, SlidersHorizontal, Wifi, WifiOff, Sparkles, Trophy, Coffee, Music2,
  Dumbbell, BookOpen, MapPin, X, Globe2, Coins as CoinsIcon, Crown, Zap,
  Camera, Upload, Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CATS_BASE = [
  { id: "all", icon: Sparkles, color: "linear-gradient(135deg, #FFFFFF, #F4F2ED)" },
  { id: "sports", icon: Dumbbell, color: "linear-gradient(135deg, #DDF5EA, #BFE9D6)" },
  { id: "social", icon: Coffee, color: "linear-gradient(135deg, #FFE6E0, #FFCFC4)" },
  { id: "educational", icon: BookOpen, color: "linear-gradient(135deg, #ECE6FF, #D9CFFF)" },
  { id: "entertainment", icon: Music2, color: "linear-gradient(135deg, #FFF1D6, #FFE08A)" },
];

const COUNTRY_PRESETS = [
  "Germany", "United States", "United Kingdom", "France", "Spain", "Italy",
  "Netherlands", "Sweden", "Canada", "Australia", "India", "Brazil", "Japan",
];

export default function Feed() {
  const { t } = useTranslation();
  const CATS = useMemo(() => CATS_BASE.map((c) => ({ ...c, label: t(`feedPage.categories.${c.id}`) })), [t]);
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [online, setOnline] = useState("all");
  const [search, setSearch] = useState("");
  const [hasCap, setHasCap] = useState(false);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [whereOpen, setWhereOpen] = useState(false);
  const [draftCity, setDraftCity] = useState("");
  const [draftCountry, setDraftCountry] = useState("");
  const [showAvatarPrompt, setShowAvatarPrompt] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const whereRef = useRef(null);

  // Prompt user for avatar if none is set, once per session
  useEffect(() => {
    if (user && !user.avatar_url) {
      const alreadyChecked = sessionStorage.getItem("tanta:avatar-prompted");
      if (!alreadyChecked) {
        setShowAvatarPrompt(true);
        sessionStorage.setItem("tanta:avatar-prompted", "true");
      }
    }
  }, [user]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const MAX_AVATAR_BYTES = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error(t("profile.fileTooLarge", { max: "10MB" }));
      return;
    }
    
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      
      const { data } = await api.post("/uploads/media", fd, {
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        }
      });
      
      await api.patch("/users/me", { avatar_url: data.url });
      await refresh();
      toast.success(t("profile.uploaded") || "Profile picture updated!");
      setShowAvatarPrompt(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed, please try again");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat !== "all") params.category = cat;
      if (online !== "all") params.is_online = online === "true";
      if (hasCap) params.has_capacity = true;
      if (city.trim()) params.city = city.trim();
      if (country.trim()) params.country = country.trim();
      const { data } = await api.get("/activities", { params });
      setItems(data);
    } catch (e) {
      // 401s are handled globally (interceptor → /auth). For everything else
      // fall through to an empty list rather than crashing the page.
      if (!e?.__silent401) console.warn("Feed load failed:", e?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, online, hasCap, city, country]);

  // Close popover on outside click
  useEffect(() => {
    if (!whereOpen) return;
    const onClick = (e) => { if (whereRef.current && !whereRef.current.contains(e.target)) setWhereOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [whereOpen]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((a) =>
      a.title.toLowerCase().includes(s) ||
      a.description?.toLowerCase?.().includes(s) ||
      a.location?.toLowerCase?.().includes(s)
    );
  }, [items, search]);

  const openWhere = () => {
    setDraftCity(city);
    setDraftCountry(country);
    setWhereOpen(true);
  };
  const applyWhere = () => {
    setCity(draftCity);
    setCountry(draftCountry);
    setWhereOpen(false);
  };
  const clearWhere = () => {
    setCity(""); setCountry("");
    setDraftCity(""); setDraftCountry("");
    setWhereOpen(false);
  };

  const whereLabel = city && country ? `${city}, ${country}` : city || country || t("feedPage.anywhere");
  const whereActive = !!(city || country);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
      <header className="mb-5 flex items-start justify-between gap-3 pr-36 sm:pr-0">
        <div>
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-[#8A8F98]">Hey {user?.name?.split(" ")[0]} 👋</p>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight">{t("feedPage.title")}</h1>
        </div>
        <img src={"https://customer-assets." + atob("ZW1lcmdlbnRhZ2VudC5jb20=") + "/job_activity-squad-2/artifacts/4cw1jeaq_1a81f51e-4e78-47f6-81c3-cfddeb7a76dc.png"} alt="TANTA" className="hidden sm:block w-14 h-14 object-contain shrink-0 mt-1" draggable={false}/>
      </header>

      {/* Quick chips: coins · VIP · spontaneous */}
      <div className="flex gap-2 mb-5 overflow-x-auto scroll-hide pb-1">
        <Link to="/coins" data-testid="chip-coins" className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FFF1D6] to-[#FFE08A] border border-[#0F1115]/8 shadow-soft hover:-translate-y-0.5 transition-all">
          <CoinsIcon className="w-4 h-4" strokeWidth={2.5} fill="#F9C349"/>
          <span className="font-display font-bold text-sm">{user?.coins ?? 0}</span>
          <span className="text-[10px] text-[#4A3000] font-semibold uppercase tracking-wider">T-Coins</span>
        </Link>
        <Link to="/vip" data-testid="chip-vip" className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0F1115]/8 shadow-soft hover:-translate-y-0.5 transition-all ${user?.is_vip ? "bg-[#0F1115] text-white" : "bg-white"}`}>
          <Crown className="w-4 h-4" strokeWidth={2.5} fill={user?.is_vip ? "#F9C349" : "none"} color={user?.is_vip ? "#F9C349" : "currentColor"}/>
          <span className="font-display font-bold text-sm">{user?.is_vip ? t("feedPage.vipBadge") : t("feedPage.goVip")}</span>
        </Link>
        <Link to="/spontaneous" data-testid="chip-now" className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#DEF1F5] to-[#C3E5EC] border border-[#0F1115]/8 shadow-soft hover:-translate-y-0.5 transition-all">
          <Zap className="w-4 h-4" strokeWidth={2.5}/>
          <span className="font-display font-bold text-sm">{t("feedPage.now")}</span>
        </Link>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#8A8F98] pointer-events-none" strokeWidth={2.25} />
          <input
            className="input-nb pl-11"
            placeholder={t("feedPage.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="feed-search-input"
          />
        </div>

        {/* Where pill */}
        <div className="relative" ref={whereRef}>
          <button
            onClick={() => (whereOpen ? setWhereOpen(false) : openWhere())}
            className={`btn-nb !pl-4 !pr-4 ${whereActive ? "btn-nb-primary" : ""}`}
            data-testid="feed-where-btn"
          >
            <MapPin className="w-4 h-4" strokeWidth={2.25} />
            <span className="max-w-[180px] truncate">{whereLabel}</span>
            {whereActive && (
              <X
                className="w-4 h-4 ml-1 opacity-80 hover:opacity-100"
                strokeWidth={2.5}
                onClick={(e) => { e.stopPropagation(); clearWhere(); }}
                data-testid="feed-where-clear"
              />
            )}
          </button>

          {whereOpen && (
            <div
              className="absolute right-0 mt-2 z-50 w-[320px] glass-strong rounded-2xl shadow-soft-lg p-5 animate-pop"
              data-testid="feed-where-popover"
            >
              <div className="flex items-center gap-2 mb-3">
                <Globe2 className="w-4 h-4 text-[#14C8B0]" strokeWidth={2.5}/>
                <span className="font-display font-semibold tracking-tight">{t("feedPage.where")}</span>
              </div>

              <label className="block text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#8A8F98] mb-1">{t("feedPage.city")}</label>
              <input
                className="input-nb mb-3"
                placeholder="e.g. Lübeck"
                value={draftCity}
                onChange={(e) => setDraftCity(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyWhere()}
                autoFocus
                data-testid="feed-city-input"
              />

              <label className="block text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#8A8F98] mb-1">{t("feedPage.country")}</label>
              <input
                className="input-nb mb-2"
                placeholder="e.g. Germany"
                value={draftCountry}
                onChange={(e) => setDraftCountry(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyWhere()}
                list="country-presets"
                data-testid="feed-country-input"
              />
              <datalist id="country-presets">
                {COUNTRY_PRESETS.map((c) => <option key={c} value={c}/>)}
              </datalist>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {COUNTRY_PRESETS.slice(0, 6).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDraftCountry(c)}
                    className={`tag-nb cursor-pointer transition-all ${draftCountry === c ? "bg-[#0F1115] text-white border-transparent" : "hover:bg-[#0F1115]/5"}`}
                    data-testid={`country-preset-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <button onClick={clearWhere} className="btn-nb flex-1" data-testid="feed-where-reset">{t("feedPage.reset")}</button>
                <button onClick={applyWhere} className="btn-nb btn-nb-primary flex-1" data-testid="feed-where-apply">{t("feedPage.apply")}</button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setHasCap((v) => !v)}
          className={`btn-nb ${hasCap ? "btn-nb-warn" : ""}`}
          data-testid="feed-has-capacity-toggle"
        >
          <SlidersHorizontal className="w-4 h-4" strokeWidth={2.25} /> {hasCap ? t("feedPage.openSpots") : t("feedPage.allFilter")}
        </button>
      </div>

      {/* Online / IRL toggle */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => setOnline("all")} className={`btn-nb !py-1.5 !text-xs ${online === "all" ? "btn-nb-dark" : ""}`} data-testid="online-filter-all">{t("feedPage.allFilter")}</button>
        <button onClick={() => setOnline("false")} className={`btn-nb !py-1.5 !text-xs ${online === "false" ? "btn-nb-coral" : ""}`} data-testid="online-filter-irl">
          <WifiOff className="w-3.5 h-3.5" strokeWidth={2.5}/> IRL
        </button>
        <button onClick={() => setOnline("true")} className={`btn-nb !py-1.5 !text-xs ${online === "true" ? "btn-nb-info" : ""}`} data-testid="online-filter-online">
          <Wifi className="w-3.5 h-3.5" strokeWidth={2.5}/> Online
        </button>
      </div>

      {/* Categories */}
      <div className="flex gap-3 overflow-x-auto scroll-hide mb-6 pb-2">
        {CATS.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            data-testid={`cat-${c.id}`}
            className={`shrink-0 flex flex-col items-center justify-center gap-1 px-5 py-3 rounded-2xl font-display font-semibold transition-all border ${
              cat === c.id ? "shadow-soft-md -translate-y-0.5 border-[#0F1115]/15" : "shadow-soft border-[#0F1115]/8 hover:-translate-y-0.5"
            }`}
            style={{ background: c.color }}
          >
            <c.icon className="w-[18px] h-[18px]" strokeWidth={2.25} />
            <span className="text-xs tracking-tight">{c.label}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-nb p-5 h-56 bg-gradient-to-br from-[#F4F2ED] to-[#EDEAE2] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-nb p-10 text-center" style={{ background: "linear-gradient(135deg, #FFF1D6, #FFE08A)" }} data-testid="feed-empty">
          <Trophy className="w-12 h-12 mx-auto mb-3" strokeWidth={2.25} />
          <h3 className="font-display font-extrabold text-2xl mb-1 tracking-tight">
            {whereActive ? "Nothing here yet" : "It's quiet out here."}
          </h3>
          <p className="text-[#4A4F58]">
            {whereActive
              ? <>No activities in <b>{whereLabel}</b>. Try widening your search or be the first.</>
              : <>Be the first to start something. Tap <b>+</b> below.</>}
          </p>
          {whereActive && (
            <button onClick={clearWhere} className="btn-nb mt-4" data-testid="feed-empty-reset">
              <X className="w-4 h-4" strokeWidth={2.5}/> Clear location
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a, i) => <ActivityCard key={a.id} activity={a} idx={i} />)}
        </div>
      )}
      {/* Avatar upload prompt modal */}
      {showAvatarPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fade-in">
          <div className="card-nb max-w-sm w-full bg-white p-6 sm:p-8 flex flex-col items-center text-center space-y-6 shadow-soft-lg">
            <div className="w-20 h-20 bg-[#DDF5EA] rounded-full flex items-center justify-center border-2 border-[#0A0B0D] shadow-nb-sm">
              <Camera className="w-10 h-10 text-[#0F9D8F]" strokeWidth={2} />
            </div>
            
            <div className="space-y-2">
              <h2 className="font-display font-black text-2xl text-[#0A0B0D]">Add a Profile Picture</h2>
              <p className="text-sm text-[#7C828D] font-bold leading-relaxed">
                Show your face so friends can recognize you. You can change this later in settings.
              </p>
            </div>
            
            <div className="w-full space-y-3">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="avatar-prompt-upload-input"
                onChange={handleAvatarUpload}
                disabled={uploading}
              />
              
              <button
                type="button"
                disabled={uploading}
                onClick={() => document.getElementById("avatar-prompt-upload-input").click()}
                className="w-full h-12 btn-nb btn-nb-primary flex items-center justify-center gap-2 text-sm font-bold shadow-nb-sm hover:translate-y-[1px] active:translate-y-[2px] transition-all disabled:opacity-60"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Uploading {progress}%</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" strokeWidth={2.5} />
                    <span>Upload Photo</span>
                  </>
                )}
              </button>
              
              <button
                type="button"
                disabled={uploading}
                onClick={() => setShowAvatarPrompt(false)}
                className="text-xs text-[#7C828D] hover:text-[#0A0B0D] font-extrabold tracking-wider transition-colors pt-1"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
